import { useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { SCUIContent } from '@/atomic/organisms/SCUIContent/SCUIContent';

import {
    getOriginFromIframeSrc,
    postAccessTokenToIframe,
    REQUEST_IFRAME_ACCESS_TOKEN_MESSAGE_TYPE,
} from '@/utils/iframeAccessTokenBridge';
import { PCI } from './postCheckoutInsights.classes';

const getFixInsightsBaseUrl = (): string => {
    const envName = (import.meta.env.VITE_ENVIRONMENT || '').toString().toLowerCase();

    if (envName === 'preprod') {
        return 'https://fixinsights-preprod.lcwaikiki.com/';
    }
    if (['local', 'dev', 'test'].includes(envName)) {
        return 'https://fixinsights-test.lcwaikiki.com/';
    }
    return 'https://fixinsights.lcwaikiki.com/';
};

export const PostCheckoutInsights = () => {
    const iframeSrc = getFixInsightsBaseUrl();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const iframeOrigin = getOriginFromIframeSrc(iframeSrc);

    const sendTokenToIframe = useCallback(() => {
        const contentWindow = iframeRef.current?.contentWindow;
        if (!iframeOrigin || !contentWindow) {
            return;
        }
        postAccessTokenToIframe(contentWindow, iframeOrigin);
    }, [iframeOrigin]);

    useEffect(() => {
        if (!iframeOrigin) {
            return;
        }

        const onMessage = (event: MessageEvent) => {
            if (event.source !== iframeRef.current?.contentWindow) {
                return;
            }
            if (event.origin !== iframeOrigin) {
                return;
            }
            if (event.data?.type === REQUEST_IFRAME_ACCESS_TOKEN_MESSAGE_TYPE) {
                postAccessTokenToIframe(event.source as Window, iframeOrigin);
            }
        };

        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [iframeOrigin]);

    return (
        <SCUIContent className={PCI.page}>
            <Box
                className={PCI.frameCard}
                sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: 'calc(100vh - 160px)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Box
                    ref={iframeRef}
                    className={PCI.iframe}
                    component="iframe"
                    title="Post Checkout Insights"
                    src={iframeSrc}
                    onLoad={sendTokenToIframe}
                    sx={{ width: '100%', height: '725px', border: 0 }}
                />
            </Box>
        </SCUIContent>
    );
};
